export interface DenOption {
  /** Human-friendly chip label. */
  label: string;
  /** The raw event.location strings this option represents (added to filters.dens on select). */
  matchValues: string[];
}

export interface DenSelectorModel {
  dens: DenOption[];
  outings: DenOption[];
}

interface CanonicalDen {
  label: string;
  key: string;
}

// Canonical Dens in display order. `key` is the normalised form used to merge
// camelCase enum leaks ("AberdeenDen") with display names ("Aberdeen Den").
const CANONICAL_DENS: CanonicalDen[] = [
  { label: 'Aberdeen Den', key: 'aberdeenden' },
  { label: 'Livingston Den', key: 'livingstonden' },
  { label: 'Glasgow Pop Up', key: 'glasgowpopup' },
  { label: 'Fife Pop Up', key: 'fifepopup' },
];

const normaliseKey = (s: string): string => s.toLowerCase().replace(/[^a-z0-9]/g, '');

export function buildDenSelectorModel(rawLocations: string[]): DenSelectorModel {
  const denMatchValues = new Map<string, string[]>(); // canonical label -> raw values
  const outingsByLabel = new Map<string, string[]>(); // outing label -> raw values

  for (const raw of rawLocations) {
    if (!raw || raw.trim() === '') continue;
    const key = normaliseKey(raw);
    const canonical = CANONICAL_DENS.find((d) => d.key === key);
    if (canonical) {
      const list = denMatchValues.get(canonical.label) ?? [];
      if (!list.includes(raw)) list.push(raw);
      denMatchValues.set(canonical.label, list);
    } else {
      const label = raw.trim();
      const list = outingsByLabel.get(label) ?? [];
      if (!list.includes(raw)) list.push(raw);
      outingsByLabel.set(label, list);
    }
  }

  const dens: DenOption[] = CANONICAL_DENS.filter((d) => denMatchValues.has(d.label)).map((d) => ({
    label: d.label,
    matchValues: denMatchValues.get(d.label) as string[],
  }));

  const outings: DenOption[] = Array.from(outingsByLabel.entries())
    .map(([label, matchValues]) => ({ label, matchValues }))
    .sort((a, b) => a.label.localeCompare(b.label));

  return { dens, outings };
}
